import React, { useEffect, useState } from "react";
import { MapPin, Trash2, Star, Smartphone, Monitor, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { addressesApi, sessionsApi, notificationsApi, NOTIF_CATEGORIES, relTime } from "@/lib/accountApi";

const input = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20";

export function AddressesSection() {
  const [items, setItems] = useState(undefined);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: "Home", line1: "", line2: "", city: "", postcode: "" });
  const [busy, setBusy] = useState(false);

  const load = () => addressesApi.list().then(setItems).catch(() => setItems(null));
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.line1.trim()) return;
    setBusy(true);
    try {
      await addressesApi.create({ ...form, line2: form.line2 || null });
      setForm({ label: "Home", line1: "", line2: "", city: "", postcode: "" });
      setAdding(false);
      toast.success("Address saved");
      load();
    } catch (err) {
      toast.error(err.message || "Couldn't save that address.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try {
      await addressesApi.remove(id);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(err.message || "Couldn't remove that.");
    }
  };
  const makeDefault = async (id) => {
    try {
      await addressesApi.setDefault(id);
      load();
    } catch (err) {
      toast.error(err.message || "Couldn't update that.");
    }
  };

  return (
    <div data-testid="addresses">
      <div className="flex items-center justify-between">
        <h2 className="font-bold">Addresses</h2>
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAdding((v) => !v)} data-testid="address-add">{adding ? "Cancel" : "Add address"}</Button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Used for deliveries, pick-ups and leave-by times in Plan my day.</p>
      {adding && (
        <form onSubmit={add} className="mt-4 grid gap-2 rounded-2xl border border-border p-4 sm:grid-cols-2">
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label (Home, Work…)" className={input} maxLength={40} />
          <input value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} placeholder="Postcode" className={input} maxLength={20} />
          <input value={form.line1} onChange={(e) => setForm({ ...form, line1: e.target.value })} placeholder="Address line 1" required className={`${input} sm:col-span-2`} maxLength={160} data-testid="address-line1" />
          <input value={form.line2} onChange={(e) => setForm({ ...form, line2: e.target.value })} placeholder="Address line 2 (optional)" className={input} maxLength={160} />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Town or city" className={input} maxLength={80} />
          <div className="sm:col-span-2"><Button type="submit" className="rounded-full" disabled={busy || !form.line1.trim()} data-testid="address-save">{busy ? "Saving…" : "Save address"}</Button></div>
        </form>
      )}
      <div className="mt-4">
        {items === undefined ? (
          <Skeleton className="h-16 rounded-2xl" />
        ) : items === null ? (
          <p className="text-sm text-muted-foreground">Couldn't load your addresses.</p>
        ) : items.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No saved addresses yet.</p>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
            {items.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-4 py-3" data-testid={`address-${a.id}`}>
                <MapPin size={16} className="shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{a.label} {a.is_default && <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold">Default</span>}</p>
                  <p className="truncate text-sm text-muted-foreground">{a.formatted || [a.line1, a.line2, a.city, a.postcode].filter(Boolean).join(", ")}</p>
                </div>
                {!a.is_default && <Button size="icon" variant="ghost" className="rounded-full" onClick={() => makeDefault(a.id)} aria-label="Make default"><Star size={16} /></Button>}
                <Button size="icon" variant="ghost" className="rounded-full text-rose-600" onClick={() => remove(a.id)} aria-label="Remove address"><Trash2 size={16} /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function DevicesSection({ onSignedOutEverywhere }) {
  const [data, setData] = useState(undefined);
  const load = () => sessionsApi.list().then(setData).catch(() => setData(null));
  useEffect(() => {
    load();
  }, []);
  const revoke = async (s) => {
    try {
      const r = await sessionsApi.revoke(s.id);
      if (r.current) onSignedOutEverywhere?.();
      else setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== s.id) }));
      toast.success("Signed out of that device");
    } catch (err) {
      toast.error(err.message || "Couldn't sign out that device.");
    }
  };
  const revokeAll = async () => {
    try {
      await sessionsApi.revokeAll();
      onSignedOutEverywhere?.();
    } catch (err) {
      toast.error(err.message || "Couldn't sign out everywhere.");
    }
  };
  return (
    <div data-testid="devices">
      <div className="flex items-center justify-between">
        <h2 className="font-bold">Devices</h2>
        <Button size="sm" variant="outline" className="rounded-full" onClick={revokeAll} data-testid="devices-revoke-all"><LogOut size={14} className="mr-1.5" /> Sign out everywhere</Button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Every phone and browser that's still signed in to your account.</p>
      <div className="mt-4">
        {data === undefined ? (
          <Skeleton className="h-16 rounded-2xl" />
        ) : data === null ? (
          <p className="text-sm text-muted-foreground">Couldn't load your devices.</p>
        ) : (
          <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border">
            {data.items.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid={`device-${s.id}`}>
                {s.platform === "web" ? <Monitor size={16} className="shrink-0 text-muted-foreground" /> : <Smartphone size={16} className="shrink-0 text-muted-foreground" />}
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{s.device_name || s.platform} {s.current && <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">This device</span>}</p>
                  <p className="text-sm text-muted-foreground">{[s.location, s.last_active_at ? `active ${relTime(s.last_active_at)} ago` : null].filter(Boolean).join(" · ")}</p>
                </div>
                <Button size="sm" variant="ghost" className="rounded-full" onClick={() => revoke(s)}>Sign out</Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function NotificationPrefsSection() {
  const [prefs, setPrefs] = useState(undefined);
  useEffect(() => {
    notificationsApi.prefs().then(setPrefs).catch(() => setPrefs(null));
  }, []);
  const save = async (patch, optimistic) => {
    const prev = prefs;
    setPrefs((p) => ({ ...p, ...optimistic }));
    try {
      setPrefs(await notificationsApi.setPrefs(patch));
    } catch (err) {
      setPrefs(prev);
      toast.error(err.message || "Couldn't save that.");
    }
  };
  if (prefs === undefined) return <Skeleton className="h-40 rounded-2xl" />;
  if (prefs === null) return <p className="text-sm text-muted-foreground">Couldn't load your notification settings.</p>;
  const qh = prefs.quiet_hours || {};
  return (
    <div data-testid="notif-prefs">
      <h2 className="font-bold">Notifications</h2>
      <p className="mt-1 text-sm text-muted-foreground">Choose what reaches you. Security alerts always do.</p>
      <ul className="mt-4 divide-y divide-border overflow-hidden rounded-2xl border border-border">
        {NOTIF_CATEGORIES.map((c) => (
          <li key={c.key} className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="font-medium">{c.label}</p>
              <p className="text-sm text-muted-foreground">{c.sub}</p>
            </div>
            <Switch checked={!!prefs.types?.[c.key]} onCheckedChange={(v) => save({ types: { [c.key]: v } }, { types: { ...prefs.types, [c.key]: v } })} aria-label={c.label} data-testid={`pref-${c.key}`} />
          </li>
        ))}
      </ul>
      <div className="mt-4 rounded-2xl border border-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="font-medium">Quiet hours</p>
            <p className="text-sm text-muted-foreground">Nothing buzzes between {String(qh.start_hour ?? 22).padStart(2, "0")}:00 and {String(qh.end_hour ?? 7).padStart(2, "0")}:00{qh.tz ? ` (${qh.tz})` : ""}.{qh.locked ? " Set by Fable + for teen accounts." : ""}</p>
          </div>
          <Switch checked={!!qh.enabled} disabled={!!qh.locked} onCheckedChange={(v) => save({ quiet_hours: { enabled: v, start_hour: qh.start_hour ?? 22, end_hour: qh.end_hour ?? 7 } }, { quiet_hours: { ...qh, enabled: v } })} aria-label="Quiet hours" data-testid="pref-quiet" />
        </div>
        {qh.enabled && !qh.locked && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <label>From <select value={qh.start_hour ?? 22} onChange={(e) => save({ quiet_hours: { enabled: true, start_hour: Number(e.target.value), end_hour: qh.end_hour ?? 7 } }, {})} className="ml-1 rounded-lg border border-border bg-background px-2 py-1">{Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select></label>
            <label>to <select value={qh.end_hour ?? 7} onChange={(e) => save({ quiet_hours: { enabled: true, start_hour: qh.start_hour ?? 22, end_hour: Number(e.target.value) } }, {})} className="ml-1 rounded-lg border border-border bg-background px-2 py-1">{Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select></label>
          </div>
        )}
      </div>
      <div className="mt-4 rounded-2xl border border-border p-4">
        <p className="font-medium">Bell badge</p>
        <div className="mt-2 flex gap-2">
          {[["count", "Show the number"], ["dot", "Just a dot"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => save({ badge_style: k }, { badge_style: k })} aria-pressed={prefs.badge_style === k} className={`rounded-full border px-3 py-1.5 text-sm font-medium ${prefs.badge_style === k ? "border-transparent bg-foreground text-background" : "border-border hover:bg-muted"}`}>{l}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
